/* globals window, CSS, requestAnimationFrame */

import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints, forms } from '@birdi/theme';
import { organisation as organisationApi, stroot } from '@birdi/js-sdk';
import { useMetaSelector } from '../../meta/reducer';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ChevronLeftSVG from '@birdi/icons/chevron-left.svg';
import ChevronRightSVG from '@birdi/icons/chevron-right.svg';
import Select from 'react-select';
import { addDays, addWeeks, addMonths, addYears, subDays, subWeeks, subMonths, subYears, format } from 'date-fns';
import { posthog } from '../../lib/posthog';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

const durationOptions = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const emptyData = (duration: string): any[] => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months.map(month => ({
    timing: month,
    filesUploaded: -1,
    mapsCreated: -1,
    shareLinksCreated: -1,
    jobsProcessed: -1
  }));
};

// Custom Tooltip Component
interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  scale: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, scale }) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    return (
      <div css={css`
        background: ${theme.mono0};
        border: 1px solid ${theme.mono20};
        border-radius: 8px;
        padding: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      `}>
        <p css={css`margin: 0 0 8px 0; font-weight: bold; color: ${theme.midnight};`}>
          {label}
        </p>
        {payload.map((entry: any, index: number) => {
          let value = entry.value;
          let name = entry.name;

          // Format metric names with proper capitalization and spacing
          switch (entry.dataKey) {
            case 'filesUploaded':
              name = 'Files uploaded';
              // Multiply filesUploaded by scale value if it exists
              if (scale.includes('/')) {
                const scaleValue = parseInt(scale.match(/\/\s*(\d+)/)?.[1] || '1');
                value = entry.value * scaleValue;
              }
              break;
            case 'mapsCreated':
              name = 'Maps created';
              break;
            case 'shareLinksCreated':
              name = 'Share links created';
              break;
            case 'jobsProcessed':
              name = 'Jobs processed';
              break;
            default:
              name = entry.name;
          }

          return (
            <p key={index} css={css`
              margin: 0;
              color: ${entry.color};
              font-size: 12px;
            `}>
              {name}: {value}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

const dateRangeButtonStyles = (theme: BirdiTheme, isDisabled: boolean) => css`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.midnight};
  padding: 0.25rem;
  border-radius: 4px;
  &:hover {
    background-color: ${theme.sand};
  };
  ${isDisabled && css`
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background-color: transparent;
    }
  `}
`;

const activitySummaryStyles = (theme: BirdiTheme) => css`
  flex: 1;
  border: solid 1px ${theme.mono20};
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ActivityTrends: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const meta = useMetaSelector();

  const [duration, setDuration] = useState<string>('yearly');
  const [chartData, setChartData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [chartDataLoading, setChartDataLoading] = useState(true);
  const [currentDateRange, setCurrentDateRange] = useState('');
  const [currentEndDate, setCurrentEndDate] = useState(new Date());

  const [isDurationDisabled, setIsDurationDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isPrevDisabled, setIsPrevDisabled] = useState(false);

  const [scale, setScale] = useState('');

  const [loadedOnce, setLoadedOnce] = useState(false);

  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [activeUsersLoading, setActiveUsersLoading] = useState(true);

  const workspaceId = meta.workspace?.id || meta.organisation.id;

  const updateDateRangeAndGetData = (duration: string, direction?: 'prev' | 'next') => {
    let startDate: Date;
    let endDate: Date;

    if (direction) {
      // Handle navigation - move both start and end dates
      if (duration === 'daily') {
        endDate = direction === 'next'
          ? addDays(currentEndDate, 1)
          : subDays(currentEndDate, 1);
        startDate = endDate; // Same day for daily
      } else if (duration === 'weekly') {
        endDate = direction === 'next'
          ? addWeeks(currentEndDate, 1)
          : subWeeks(currentEndDate, 1);
        startDate = subDays(endDate, 6); // 7 days ending at endDate
      } else if (duration === 'monthly') {
        if (direction === 'next') {
          endDate = addMonths(currentEndDate, 1);
        } else {
          endDate = subMonths(currentEndDate, 1);
        }
        startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1); // 1st day of target month
        endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0); // Last day of target month
      } else if (duration === 'yearly') {
        if (direction === 'next') {
          endDate = addYears(currentEndDate, 1);
        } else {
          endDate = subYears(currentEndDate, 1);
        }
        startDate = new Date(endDate.getFullYear(), 0, 1); // January 1st of the target year
        endDate = new Date(endDate.getFullYear(), 11, 31); // December 31st of the target year
      } else {
        endDate = new Date();
        startDate = new Date();
      }
    } else {
      // Initial load or duration change - show complete periods that include today
      const today = new Date();
      if (duration === 'daily') {
        startDate = today; // Today
        endDate = today;
      } else if (duration === 'weekly') {
        // Find the start of the week (Monday) that includes today
        const dayOfWeek = today.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so convert to Monday = 0
        startDate = subDays(today, daysFromMonday);
        endDate = addDays(startDate, 6); // Sunday
      } else if (duration === 'monthly') {
        // Find the first and last days of the current month
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of current month
      } else if (duration === 'yearly') {
        // Find the start of the year that includes today
        startDate = new Date(today.getFullYear(), 0, 1); // January 1st
        endDate = new Date(today.getFullYear(), 11, 31); // December 31st
      } else {
        startDate = today;
        endDate = today;
      }
    }
    setCurrentEndDate(endDate); // Store current end date

    // Calculate and format the date range
    if (duration === 'daily') {
      setCurrentDateRange(format(startDate, 'do MMMM yyyy'));
    } else if (duration === 'weekly') {
      setCurrentDateRange(`${format(startDate, 'do MMMM')} - ${format(endDate, 'do MMMM')}`);
    } else if (duration === 'monthly') {
      setCurrentDateRange(format(startDate, 'MMMM yyyy'));
    } else if (duration === 'yearly') {
      setCurrentDateRange(format(startDate, 'yyyy'));
    }

    // Check if the current date range includes today
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    const isRangeIncludesToday = startDate <= todayEnd && endDate >= todayStart;
    setIsNextDisabled(isRangeIncludesToday);

    // // Check if start date would go before 2023 to disable prev button
    // const minDate = new Date('2023-12-31');
    // const isStartDateBefore2023 = startDate < minDate;
    // setIsPrevDisabled(isStartDateBefore2023);

    getChartData(duration, startDate, endDate);
    getActiveUsersData(duration, startDate, endDate);
  };

  const getChartData = async (duration: string, startDate: Date, endDate: Date) => {
    setChartDataLoading(true);
    try {
      const res = await organisationApi.getActivityTrends(
        stroot('ataex6'),
        duration,
        startDate,
        endDate
      );
      const timingDataResponse = Array.isArray(res.body) ? res.body : [];
      const scaleValue = getScale(timingDataResponse);
      const scaledData = scaleData(timingDataResponse, scaleValue);
      const transformedData = transformTimingLabels(scaledData, duration);
      setChartData(transformedData);

      // Store original data with timing labels for peak calculation
      const originalDataWithLabels = transformTimingLabels(timingDataResponse, duration);
      setOriginalData(originalDataWithLabels);

      if (Array.isArray(timingDataResponse) && timingDataResponse.length > 0 && timingDataResponse.some(item => item.filesUploaded > 0 || item.mapsCreated > 0 || item.shareLinksCreated > 0 || item.jobsProcessed > 0)) {
        setLoadedOnce(true);
        setIsDurationDisabled(false);
      }
    } catch (e) {
      setChartData(emptyData(duration));
    }
    setChartDataLoading(false);
  }

  const getActiveUsersData = async (duration: string, startDate: Date, endDate: Date) => {
    setActiveUsersLoading(true);
    try {
      const res = await organisationApi.getActiveUsers(
        stroot('ohsii5'),
        startDate,
        endDate
      );
      const activeUsersResponse = Array.isArray(res.body) ? res.body : [];
      setActiveUsers(activeUsersResponse);
    } catch (e) {
      setActiveUsers([]);
    }
    setActiveUsersLoading(false);
  }

  useEffect(() => {
    updateDateRangeAndGetData(duration);
  }, [workspaceId]);

  const handleDurationChange = (option: any) => {
    setDuration(option.value);
    setCurrentEndDate(new Date()); // Reset to current date when duration changes
    updateDateRangeAndGetData(option.value);
    posthog?.capture('dashboard activity trends chart duration clicked', { duration: option.value });
  };

  const handleDateRangeChange = (direction: 'prev' | 'next') => {
    updateDateRangeAndGetData(duration, direction);
  };

  const getScale = (data: any[]): number => {
    if (!data || data.length === 0) {
      setScale('');
      return 0;
    }

    // Find max values
    const maxFilesUploaded = Math.max(...data.map(item => item.filesUploaded || 0));
    const maxOthers = Math.max(
      ...data.map(item => Math.max(
        item.mapsCreated || 0,
        item.shareLinksCreated || 0,
        item.jobsProcessed || 0
      ))
    );

    // Avoid division by zero
    if (maxOthers === 0) {
      setScale('');
      return 0;
    }

    // Calculate ratio
    const ratio = maxFilesUploaded / maxOthers;

    // Determine scale based on ratio
    let scaleValue = 0;
    let result = '';
    if (ratio > 10) {
      scaleValue = 10;
      result = ` [ /10 ]`;
    }
    if (ratio > 100) {
      scaleValue = 100;
      result = ` [ /100 ]`;
    }
    if (ratio > 1000) {
      scaleValue = 1000;
      result = ` [ /1000 ]`;
    }
    setScale(result);
    return scaleValue;
  }

  const scaleData = (data: any[], scaleValue: number) => {
    if (!data || data.length === 0) {
      return [];
    }

    if (scaleValue === 0) {
      return data;
    }

    return data.map(item => ({
      ...item,
      filesUploaded: item.filesUploaded / scaleValue,
    }));
  }

  const transformTimingLabels = (data: any[], duration: string) => {
    if (!data || data.length === 0) {
      return data;
    }

    const labels = {
      daily: ['12:00am', '1:00am', '2:00am', '3:00am', '4:00am', '5:00am', '6:00am', '7:00am', '8:00am', '9:00am', '10:00am', '11:00am', '12:00pm', '1:00pm', '2:00pm', '3:00pm', '4:00pm', '5:00pm', '6:00pm', '7:00pm', '8:00pm', '9:00pm', '10:00pm', '11:00pm'],
      weekly: ['Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'],
      monthly: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'],
      yearly: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec']
    };

    const durationLabels = labels[duration as keyof typeof labels] || [];

    return data.map((item, index) => ({
      ...item,
      timing: durationLabels[index] || item.timing
    }));
  }

  const findPeakActivity = (data: any[], duration: string): string => {
    if (!data || data.length === 0) {
      return '';
    }

    // Calculate total activity for each time period
    const activityTotals = data.map(item => ({
      timing: item.timing,
      total: (item.filesUploaded || 0) + (item.mapsCreated || 0) + (item.shareLinksCreated || 0) + (item.jobsProcessed || 0)
    }));

    // Find the peak activity
    const peakActivity = activityTotals.reduce((max, current) =>
      current.total > max.total ? current : max
    );

    if (peakActivity.total === 0) {
      return '';
    }

        // Format the peak activity message based on duration
    switch (duration) {
      case 'daily':
        return `Peak team activity happened at <strong>${peakActivity.timing}</strong>`;
      case 'weekly':
        // Convert abbreviated day names to full names
        const dayLabels: { [key: string]: string } = {
          'Mon': 'Monday',
          'Tues': 'Tuesday',
          'Wed': 'Wednesday',
          'Thurs': 'Thursday',
          'Fri': 'Friday',
          'Sat': 'Saturday',
          'Sun': 'Sunday'
        };
        const dayName = dayLabels[peakActivity.timing] || peakActivity.timing;
        return `Peak team activity happened on <strong>${dayName}</strong>`;
      case 'monthly':
        // Convert chart labels to proper week names for the message
        const weekLabels: { [key: string]: string } = {
          'Week 1': 'first week',
          'Week 2': 'second week',
          'Week 3': 'third week',
          'Week 4': 'fourth week',
          'Week 5': 'last week'
        };
        const weekName = weekLabels[peakActivity.timing] || peakActivity.timing.toLowerCase();
        return `Peak team activity happened in the <strong>${weekName}</strong>`;
      case 'yearly':
        // Convert chart labels to full month names for the message
        const monthLabels: { [key: string]: string } = {
          'Jan': 'January',
          'Feb': 'February',
          'Mar': 'March',
          'Apr': 'April',
          'May': 'May',
          'June': 'June',
          'July': 'July',
          'Aug': 'August',
          'Sept': 'September',
          'Oct': 'October',
          'Nov': 'November',
          'Dec': 'December'
        };
        const monthName = monthLabels[peakActivity.timing] || peakActivity.timing;
        return `Peak team activity happened in <strong>${monthName}</strong>`;
      default:
        return `Peak team activity happened at <strong>${peakActivity.timing}</strong>`;
    }
  }

  const peakActivityMessage = findPeakActivity(originalData, duration);

  const renderPeakActivity = () => {
    const hasData = originalData && originalData.length > 0 && originalData.some(item =>
      (item.filesUploaded || 0) + (item.mapsCreated || 0) + (item.shareLinksCreated || 0) + (item.jobsProcessed || 0) > 0
    );

    return (
      <div css={activitySummaryStyles}>
        <span css={css`font-size: 1.25rem;`}>🚀</span>
        <span
          css={css`
            color: ${hasData ? theme.midnight : theme.mono50};
          `}
          dangerouslySetInnerHTML={{
            __html: peakActivityMessage || 'Peak team activity trends will appear here.'
          }}
        />
      </div>
    );
  };

  const renderActiveTeamMembers = () => {
    if (activeUsersLoading) {
      return (
        <div css={activitySummaryStyles}>
          <span>Loading...</span>
        </div>
      );
    }

    if (!activeUsers || activeUsers.length === 0) {
      return (
        <div css={activitySummaryStyles}>
          <span css={css`font-size: 1.25rem;`}>⭐</span>
          <span>Most active team members will appear here.</span>
        </div>
      );
    }

    return (
      <div css={activitySummaryStyles}>
        <div css={css`
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          width: 100%;
          color: ${theme.midnight};
        `}>
          <div css={css`
            display: flex;
            align-items: center;
            gap: 0.5rem;
          `}>
            <span css={css`font-size: 1.25rem;`}>⭐</span>
            <span>Most active team members:</span>
          </div>

          <div css={css`
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            padding: 0 1em;
          `}>
            {activeUsers.slice(0, 2).map((user, index) => (
              <div key={user.id} css={css`
                display: flex;
                align-items: center;
                gap: 0.5rem;
              `}>
                <div css={css`
                  width: 2rem;
                  height: 2rem;
                  min-width: 2rem;
                  min-height: 2rem;
                  border-radius: 50%;
                  background: ${theme.lightOcean};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-weight: bold;
                  font-size: 0.8rem;
                  flex-shrink: 0;
                  aspect-ratio: 1;
                `}>
                  {user.profilePic ? (
                    <img
                      src={user.profilePic}
                      alt={`${user.fname} ${user.lname}`}
                      css={css`
                        width: 100%;
                        height: 100%;
                        border-radius: 50%;
                        object-fit: cover;
                      `}
                    />
                  ) : (
                    `${user.fname.charAt(0)}${user.lname.charAt(0)}`
                  )}
                </div>
                <span css={css`
                  font-weight: bold;
                `}>
                  {user.fname} {user.lname}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
        id={suppressIntercomAnchor ? undefined : 'activity-trends'}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Activity trends</span>
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        `}
      >
        <div css={css`
            margin-bottom: 1em;
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <span>Workspace activity over time</span>
          <Select
            isDisabled={isDurationDisabled}
            options={durationOptions}
            value={{ value: duration, label: duration.charAt(0).toUpperCase() + duration.slice(1) }}
            onChange={handleDurationChange}
            styles={{
              ...forms.reactSelect,
              valueContainer: (provided) => ({
                ...provided,
                padding: '5px',
                minWidth: '80px',
              }),
              singleValue: (provided) => ({
                ...provided,
                color: theme.midnight,
              }),
              menu: (provided) => ({
                ...provided,
                zIndex: 11,
              }),
              control: (provided, state) => ({
                ...provided,
                borderColor: theme.mono40,
                boxShadow: 'none',
                '&:hover': {
                  borderColor: theme.midnight,
                  boxShadow: `0 0 1px 1px ${theme.mono30}`,
                },
                '&:focus': {
                  borderColor: theme.mono40,
                },
              }),
            }}
            isSearchable={false}
          />
        </div>

        {/* Chart Area */}
        <div
          css={css`
            min-height: 210px;
            position: relative;
            background: ${theme.mono10};
            border: 1px solid ${theme.mono10};
            border-radius: 8px;
            display: flex;
            flex-direction: column;
          `}
        >
          {/* Date Navigation */}
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              margin: 1em 0;
            `}
          >
            <button
              css={dateRangeButtonStyles(theme, isDurationDisabled || isPrevDisabled)}
              onClick={() => handleDateRangeChange('prev')}
              disabled={isDurationDisabled || isPrevDisabled}
            >
              <ChevronLeftSVG />
            </button>
            <span css={css`font-weight: 500; margin: 0 1rem;`}>{currentDateRange}</span>
            <button
              onClick={() => handleDateRangeChange('next')}
              disabled={isDurationDisabled || isNextDisabled}
              css={dateRangeButtonStyles(theme, isDurationDisabled || isNextDisabled)}
            >
              <ChevronRightSVG />
            </button>
          </div>

          {!chartDataLoading && (
            <div css={css`
                width: 100%;
                height: 300px;
                position: relative;
              `}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 40, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="2 2" stroke={theme.mono30} />
                  <XAxis dataKey="timing" stroke={theme.mono40} fontSize={13} />
                  <YAxis stroke={theme.mono40} fontSize={13} />
                  <Tooltip
                    content={<CustomTooltip scale={scale} />}
                  />
                  {/* <Legend /> */}
                  {chartData.some(item => item.filesUploaded > 0 || item.mapsCreated > 0 || item.shareLinksCreated > 0 || item.jobsProcessed > 0) && (
                    <>
                      <Line type="monotone" dataKey="filesUploaded" stroke="#FFCA46" strokeWidth={2} animationDuration={1000} />
                      <Line type="monotone" dataKey="mapsCreated" stroke="#2287FA" strokeWidth={2} animationDuration={1000} />
                      <Line type="monotone" dataKey="shareLinksCreated" stroke="#FC7543" strokeWidth={2} animationDuration={1000} />
                      <Line type="monotone" dataKey="jobsProcessed" stroke="#4AD294" strokeWidth={2} animationDuration={1000} />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>

              {/* No data message overlay */}
              {!loadedOnce && !chartData.some(item => item.filesUploaded > 0 || item.mapsCreated > 0 || item.shareLinksCreated > 0 || item.jobsProcessed > 0) && (
                <div css={css`
                  position: absolute;
                  top: 20%;
                  left: 20%;
                  right: 10%;
                  z-index: 10;
                  padding: 1em;
                `}>
                  <strong>No activity yet:</strong> Once you start uploading files, creating maps, or sharing links, you'll see trends appear here.
                </div>
              )}
            </div>
          )}

          {chartDataLoading && (
            <div css={css`
                height: 100%;
                min-height: 300px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: ${theme.midnight};
              `}
            >
              Loading...
            </div>
          )}
        </div>

        {/* Legend */}
        <div
          css={css`
            display: flex;
            justify-content: center;
            gap: 1rem;
            margin: 1rem;
            flex-wrap: wrap;
            div {
              div {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                div {
                  width: 10px;
                  height: 10px;
                  border-radius: 50%;
                };
                span {
                  font-size: 0.85rem;
                }
              }
            }
          `}
        >
          <div>
            <div>
              <div css={css` background-color: #FFCA46;`}></div>
              <span>{`Files uploaded${scale}`}</span>
            </div>
            <div>
              <div css={css`background-color: #2287FA;`}></div>
              <span>Maps created</span>
            </div>
          </div>
          <div>
            <div>
              <div css={css`background-color: #FC7543;`}></div>
              <span>Share links created</span>
            </div>
            <div>
              <div css={css`background-color: #4AD294;`}></div>
              <span>Jobs processed</span>
            </div>
          </div>
        </div>

        {/* Activity Summary Cards */}
        <div
          css={css`
            display: flex;
            gap: 1rem;
            // margin-top: auto;
            margin-top: 1em;
          `}
        >
          {renderPeakActivity()}
          {renderActiveTeamMembers()}
        </div>
      </div>
    </div>
  );
};
